'use client';

import { c as _c } from "react/compiler-runtime";
import { type inferParserType, parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringEnum, throttle, useQueryState, useQueryStates } from 'nuqs';
import { z } from 'zod';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { ArticleTabsEnum } from '../molecules/article-group/article-term-list.tsx';
const limitValues = ['4', '8', '12', '24'] as const;
export const ArticleLimitEnum = z.enum(limitValues);
export type ArticleLimitEnum = z.infer<typeof ArticleLimitEnum>;
const giftCatalogSortValues = ['mostNeeded', 'priceLowToHigh', 'priceHighToLow', 'alphabeticalAZ', 'alphabeticalZA'] as const;
export const GiftCatalogSortEnum = z.enum(giftCatalogSortValues);
export type GiftCatalogSortEnum = z.infer<typeof GiftCatalogSortEnum>;
const sortByValues = ['promotedContent', 'desc', 'asc'] as const;
export const ArticleSortByEnum = z.enum(sortByValues);
export type ArticleSortByEnum = z.infer<typeof ArticleSortByEnum>;
const sacFilterValues = ['orphanedChild', 'hasDisabilities', 'vulnerableExploitation', 'affectedHiv'] as const;
export const SacFiltersEnum = z.enum(sacFilterValues);
export type SacFiltersEnum = z.infer<typeof SacFiltersEnum>;
export const usePaginationQueryParams = () => {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsInteger.withDefault(0).withOptions({
      scroll: true
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [currentPage, setCurrentPage] = useQueryState("page", t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = parseAsInteger.withDefault(0);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [currentPageWithoutScroll, setCurrentPageWithoutScroll] = useQueryState("page", t1);
  let t2;
  if ($[2] !== currentPage || $[3] !== currentPageWithoutScroll || $[4] !== setCurrentPage || $[5] !== setCurrentPageWithoutScroll) {
    t2 = {
      currentPage,
      setCurrentPage,
      currentPageWithoutScroll,
      setCurrentPageWithoutScroll
    };
    $[2] = currentPage;
    $[3] = currentPageWithoutScroll;
    $[4] = setCurrentPage;
    $[5] = setCurrentPageWithoutScroll;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export const useSearchQueryParams = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withDefault("");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [searchFilters, setSearchFilters] = useQueryState("q", t0);
  let t1;
  if ($[1] !== searchFilters || $[2] !== setSearchFilters) {
    t1 = {
      searchFilters,
      setSearchFilters
    };
    $[1] = searchFilters;
    $[2] = setSearchFilters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const giftCatalogParamsSchema = {
  category: parseAsArrayOf(parseAsString, ',').withDefault([]),
  promotion: parseAsString.withDefault(''),
  priceMin: parseAsInteger.withDefault(generalConfig.catalogFilterMin),
  priceMax: parseAsInteger.withDefault(generalConfig.catalogFilterMax),
  sortby: parseAsString.withDefault(GiftCatalogSortEnum.enum.mostNeeded)
};
export const useGiftCatalogQueryParams = () => {
  const $ = _c(3);
  const [giftCatalogFilters, setGiftCatalogFilters] = useQueryStates(giftCatalogParamsSchema);
  let t0;
  if ($[0] !== giftCatalogFilters || $[1] !== setGiftCatalogFilters) {
    t0 = {
      giftCatalogFilters,
      setGiftCatalogFilters
    };
    $[0] = giftCatalogFilters;
    $[1] = setGiftCatalogFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const useTabGroupQueryParams = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withDefault(ArticleTabsEnum.enum.all);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [tabGroupFilters, setTabGroupFilters] = useQueryState("tab", t0);
  let t1;
  if ($[1] !== setTabGroupFilters || $[2] !== tabGroupFilters) {
    t1 = {
      tabGroupFilters,
      setTabGroupFilters
    };
    $[1] = setTabGroupFilters;
    $[2] = tabGroupFilters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export type ArticleQueryParams = {
  limit?: number;
  sortby?: ArticleSortByEnum;
  topic?: string;
  theme?: string;
  country?: string;
};
export const useArticleQueryParams = props => {
  const $ = _c(10);
  let t0;
  if ($[0] !== props) {
    t0 = props || {};
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    perPage
  } = t0;
  let t1;
  if ($[2] !== perPage) {
    t1 = parseAsInteger.withDefault(perPage || 12);
    $[2] = perPage;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = parseAsString.withDefault(ArticleSortByEnum.enum.desc);
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = {
      limit: t1,
      sortby: t2,
      topic: parseAsString,
      theme: parseAsString,
      country: parseAsString
    };
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const articleQueryParamsSchema = t3;
  const [articleFilters, setArticleFilters] = useQueryStates(articleQueryParamsSchema);
  let t4;
  if ($[7] !== articleFilters || $[8] !== setArticleFilters) {
    t4 = {
      articleFilters,
      setArticleFilters
    };
    $[7] = articleFilters;
    $[8] = setArticleFilters;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const {
  sacMinAge,
  sacMaxAge
} = generalConfig;
export const sacQueryParamsSchema = {
  gender: parseAsStringEnum<ChildGenderEnum>(Object.values(ChildGenderEnum)).withDefault(ChildGenderEnum.Either),
  countries: parseAsArrayOf(parseAsString.withDefault('')).withDefault([]),
  ageMin: parseAsInteger.withDefault(sacMinAge),
  ageMax: parseAsInteger.withDefault(sacMaxAge),
  birthDay: parseAsInteger.withDefault(0),
  birthMonth: parseAsInteger.withDefault(0),
  filters: parseAsArrayOf(parseAsString.withDefault(''), '%2C').withDefault([]),
  page: parseAsInteger.withDefault(0)
};
export type SacQueryParams = inferParserType<typeof sacQueryParamsSchema>;
export const useSacQueryParams = t0 => {
  const $ = _c(7);
  const {
    isLive
  } = t0;
  let t1;
  if ($[0] !== isLive) {
    t1 = isLive ? undefined : throttle(Infinity);
    $[0] = isLive;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      history: "replace",
      limitUrlUpdates: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [sacFilters, setSacFilters] = useQueryStates(sacQueryParamsSchema, t2);
  let t3;
  if ($[4] !== sacFilters || $[5] !== setSacFilters) {
    t3 = {
      sacFilters,
      setSacFilters
    };
    $[4] = sacFilters;
    $[5] = setSacFilters;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const fundQueryParamsSchema = {
  oneTime: parseAsArrayOf(parseAsInteger),
  monthly: parseAsArrayOf(parseAsInteger),
  annually: parseAsArrayOf(parseAsInteger),
  defaultFrequency: parseAsStringEnum(Object.values(CommitmentFrequencyEnum) as CommitmentFrequencyEnum[]),
  defaultOneTime: parseAsInteger,
  defaultMonthly: parseAsInteger,
  defaultAnnually: parseAsInteger
};
export type FundQueryParams = inferParserType<typeof fundQueryParamsSchema>;
export const useFundQueryParams = () => {
  const $ = _c(3);
  const [fundParams, setFundParams] = useQueryStates(fundQueryParamsSchema);
  let t0;
  if ($[0] !== fundParams || $[1] !== setFundParams) {
    t0 = {
      fundParams,
      setFundParams
    };
    $[0] = fundParams;
    $[1] = setFundParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};